/**
 * 远端音频播放：把每个 peer 的 MediaStream 挂到隐藏 <audio>。
 *
 * 若设备自检里选择了扬声器（localStorage `zongheng.audioOutputId`），
 * 这里用 setSinkId 把远端音频路由到该输出；不支持时退回系统默认。
 */
import { useCallback, useEffect, useState } from 'react';
import { useArena } from '../../state/ArenaContext.tsx';

function readOutputId(): string {
  try {
    return localStorage.getItem('zongheng.audioOutputId') ?? '';
  } catch {
    return '';
  }
}

function RemoteAudio({
  peerId,
  stream,
  deafened,
  name,
  outputId,
}: {
  peerId: string;
  stream: MediaStream;
  deafened: boolean;
  name?: string;
  outputId: string;
}) {
  const attach = useCallback(
    (element: HTMLAudioElement | null) => {
      if (!element) return;
      if (element.srcObject !== stream) element.srcObject = stream;
      element.muted = deafened;
      const sink = element as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      if (outputId && sink.setSinkId) {
        void sink.setSinkId(outputId).catch(() => {
          // 浏览器不允许/设备已拔掉：保持默认输出
        });
      }
      const result = element.play();
      if (result && typeof result.catch === 'function') result.catch(() => {});
    },
    [stream, deafened, outputId],
  );

  return (
    <audio
      ref={attach}
      data-testid={`remote-audio-${peerId}`}
      aria-label={name ? `${name} 的音频` : undefined}
      autoPlay
      playsInline
      muted={deafened}
      className="hidden"
    />
  );
}

export function RemoteAudioManager() {
  const { state, actions } = useArena();
  const streams = actions.getRemoteStreams();
  const [outputId, setOutputId] = useState(readOutputId);

  useEffect(() => {
    const onChange = (event: Event) => {
      setOutputId((event as CustomEvent<string>).detail || readOutputId());
    };
    window.addEventListener('zongheng:audio-output', onChange);
    return () => window.removeEventListener('zongheng:audio-output', onChange);
  }, []);

  return (
    <div data-testid="remote-audio-manager" aria-hidden="true">
      {streams.map(({ peerId, stream }) => {
        const participant = state.participants.find((item) => item.id === peerId);
        return (
          <RemoteAudio
            key={peerId}
            peerId={peerId}
            stream={stream}
            deafened={state.peering.deafened}
            name={participant?.name}
            outputId={outputId}
          />
        );
      })}
    </div>
  );
}
